import {
  guardTickerFilterInputs,
  sanitizeTickerFilterValue,
  TICKER_FILTER_INPUT_ATTRS,
} from "@shared/shared/tickerFilterInput";
import { attachSortIndicator } from "@shared/ui/tableSort";
import {
  fetchMarketdataOptionPopBatch,
  getCachedMarketdataOptionPop,
} from "@shared/api/marketdataOptionPop";
import {
  buildRecommendationLookup,
  buildShareAvgByTicker,
  callRiskOptyExposure,
  callRiskOptyValueClass,
  callStrikeVsAvgCost,
  callStrikeVsAvgTip,
  fmtUsd,
  fmtUsdWhole,
  fmtWheelRatio,
  invalidateWheelSnapshots,
  loadPositionsSnapshot,
  loadRecommendationsSnapshot,
  matchPositionToRecommendation,
  normalizeTicker,
  optionIsItm,
  optionMoneyness,
  assignAtmBadges,
  optionPremiumReceived,
  optionRocCollateral,
  optionStrikeCollateral,
  resolveShareAvgCostForTicker,
  type OptionPosition,
  type PositionsSnapshot,
  type StockPosition,
  type WheelRecommendation,
} from "@shared/ui/watchlistData";
import { scopeAllowsAccount, scopeAllowsTicker, subscribeAccountScope } from "@shared/ui/accountScope";
import { loadOwnLedgerStrikeAvgMap } from "@shared/ui/ownLedgerClient";
import {
  resolveOptionsPositionsSnapshot,
  sessionFeedSourceMismatch,
  type OptionsPositionsResolution,
  type PositionsFeedSource,
} from "@shared/ui/optionsPositionsFeed";
import {
  accountShortCellHtml,
  brokerMarkCellHtml,
  ensureLedgerMarkIndex,
  ledgerRowMark,
  subscribeLedgerMarks,
  type LedgerDisplayMark,
} from "@shared/ui/ledgerMarks";
import { notifyHeaderFeedQuoteRefreshed } from "@shared/ui/headerFeedIndicator";
import { renderFocusPositionRowMenu } from "@shared/ui/focusActions";
import { withPreservedInputFocus } from "@shared/ui/preserveInputFocus";
import {
  renderScreenerExpiryInnerHtml,
  screenerExpiryDateLabel,
} from "@shared/ui/screenerExpiryCell";
import { bindDetailsCollapsiblePersistence, collapsibleOpenAttr } from "@shared/ui/collapsibleSections";
import {
  displayTableColumnsForData,
  ensureColumnPickerBesideReset,
  getTableColumnDefaults,
  renderTableColgroup,
  renderTablePinnedAttr,
  renderTableHeadRow,
  type ColumnState,
} from "@shared/ui/tableColumns";
import {
  compactSubline,
  isWebCompactMode,
  resolveWebTableColumns,
} from "@shared/ui/webCompactMode";
import { renderBtcEarlyCloseDotFrom } from "@shared/ui/btcEarlyClose";
import { paintPanelLoadError } from "./authUi";
import {
  adviceRowAttrs,
  advisoryFilterFieldHtml,
  applyAdviceMarks,
  bindScreenerAdvice,
  screenerAdviceButtonHtml,
} from "@shared/ui/agentAdviceOverlay";

const FILTERS_KEY = "sfe-web-options-filters-v1";
const SORT_KEY = "sfe-web-options-sort-v1";
const TABLE_ID = "web-options-positions" as const;
const CACHE_MS = 60_000;

/** Columns still enriched from Stock Forge when own-ledger is primary. */
const SF_ENRICHED_COL_KEYS = new Set(["target", "mark"]);

const DONUT_COLORS = [
  "#ff7a18",
  "#2fbf71",
  "#5b8def",
  "#b07cff",
  "#f5a623",
  "#ef4565",
  "#3dd6d0",
  "#9aa0a6",
  "#e67e22",
  "#7b61ff",
  "#47a6ff",
  "#c9a227",
];

export interface OptionsFilterState {
  ticker: string;
  type: "all" | "put" | "call";
  moneyness: "all" | "itm" | "otm";
  sector: string;
  expiration: "all" | "expired" | "at-expiration";
  risk: "all" | "risky";
}

interface EnrichedOptionRow {
  position: OptionPosition;
  ticker: string;
  sector: string;
  avgCost: number | null;
  stockPrice: number | null;
  isCall: boolean;
  isItm: boolean;
  moneyness: "itm" | "atm" | null;
  isRisky: boolean;
  pop: number | null;
  credit: number | null;
  openRoc: number | null;
  heldRoc: number | null;
  mark: LedgerDisplayMark | null;
}

interface OptionsPanelCache {
  positions: PositionsSnapshot;
  recLookup: Map<string, WheelRecommendation>;
  stockAvgs: Map<string, number>;
  rows: EnrichedOptionRow[];
  fetchedAt: number;
  positionSource: PositionsFeedSource;
}

interface DiversitySlice {
  label: string;
  value: number;
  color: string;
  pct: number;
}

let panelCache: OptionsPanelCache | null = null;
let panelInflight: Promise<OptionsPanelCache> | null = null;
let panelGeneration = 0;
let mountedPanel: HTMLElement | null = null;
let optionsRerender: (() => void) | null = null;
subscribeAccountScope(() => optionsRerender?.());

subscribeLedgerMarks(() => {
  if (!panelCache || !mountedPanel || !panelHasDisplayedContent(mountedPanel)) return;
  refreshRowMarks(panelCache.rows);
  optionsRerender?.();
});

function optionLedgerMark(position: OptionPosition): LedgerDisplayMark | null {
  const mark = ledgerRowMark({
    account_id: position.account_id,
    account_short_name: position.account_short_name,
    account_label: position.account_label,
    broker_id: position.broker_id,
    broker_name: position.broker_name,
  });
  if (!mark) return null;
  const extraCount = Math.max(0, (position.account_count ?? 1) - 1);
  if (!extraCount) return mark;
  const extra = ` · also in ${extraCount} other account${extraCount === 1 ? "" : "s"}`;
  return { ...mark, accountTitle: `${mark.accountTitle}${extra}` };
}

function refreshRowMarks(rows: EnrichedOptionRow[]): void {
  for (const row of rows) row.mark = optionLedgerMark(row.position);
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escAttr(value: string): string {
  return esc(value).replaceAll("'", "&#39;");
}

function asNum(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function isCall(position: OptionPosition): boolean {
  return /^(call|c)$/i.test(position.side ?? "");
}

function stockByTicker(positions: PositionsSnapshot): Map<string, StockPosition> {
  const map = new Map<string, StockPosition>();
  for (const row of positions.stock ?? []) {
    map.set(normalizeTicker(row.ticker), row);
  }
  return map;
}

/** Own-ledger strike avg first (Stocks tab source), then SF/RH/rec fallbacks. */
async function buildOptionsStockAvgs(force: boolean): Promise<Map<string, number>> {
  const map = new Map<string, number>();
  try {
    const own = await loadOwnLedgerStrikeAvgMap(force);
    for (const [ticker, avg] of own) {
      if (avg > 0) map.set(ticker, avg);
    }
  } catch {
    /* own ledger optional */
  }
  const fallback = await buildShareAvgByTicker(force);
  for (const [ticker, avg] of fallback) {
    if (!map.has(ticker)) map.set(ticker, avg);
  }
  return map;
}

function optionsCacheNeedsStockAvgs(cache: OptionsPanelCache): boolean {
  if (cache.stockAvgs.size > 0) return false;
  return cache.rows.some((row) => row.isCall);
}

function resolveRowStockPrice(
  position: OptionPosition,
  ticker: string,
  positions: PositionsSnapshot,
  rec: WheelRecommendation | undefined,
  positionSource: PositionsFeedSource,
): number | null {
  const fromPosition = asNum(position.stock_price);
  if (fromPosition != null) return fromPosition;
  // Own-ledger / RH rows carry stock_price from RH equity quotes; don't fall back to SF.
  if (positionSource === "robinhood" || positionSource === "own_ledger") return null;
  return (
    asNum(rec?.underlying_price) ??
    asNum(positions.stock.find((s) => normalizeTicker(s.ticker) === ticker)?.current_price) ??
    null
  );
}

function moneynessBadgeHtml(moneyness: "itm" | "atm" | null): string {
  if (!moneyness) return "";
  const label = moneyness === "atm" ? "ATM" : "ITM";
  const cls = moneyness === "atm" ? "mny-atm" : "mny-itm";
  const title = moneyness === "atm" ? "At the money — nearest OTM strike within ~0.5% of spot" : "In the money";
  const wrapCls = "tk-money sfe-options-ticker-mny";
  return `<span class="${wrapCls}"><span class="mny ${cls}" title="${title}">${label}</span></span>`;
}

function defaultFilters(): OptionsFilterState {
  return {
    ticker: "",
    type: "all",
    moneyness: "all",
    sector: "",
    expiration: "all",
    risk: "all",
  };
}

function loadFilters(): OptionsFilterState {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return defaultFilters();
    const parsed = JSON.parse(raw) as Partial<OptionsFilterState>;
    const merged = { ...defaultFilters(), ...parsed };
    merged.ticker = sanitizeTickerFilterValue(merged.ticker).toUpperCase();
    return merged;
  } catch {
    return defaultFilters();
  }
}

function saveFilters(state: OptionsFilterState): void {
  localStorage.setItem(FILTERS_KEY, JSON.stringify(state));
}

function collateralNaMarkup(): string {
  return '<span class="sfe-collateral-na" aria-hidden="true">—</span>';
}

function formatCollateralDisplay(isCall: boolean, amount: number | null): string {
  if (isCall) return collateralNaMarkup();
  return fmtMoney(amount);
}

function collateralSublineMarkup(isCall: boolean, amount: number | null): string {
  return `col ${formatCollateralDisplay(isCall, amount)}`;
}

function computeOpenRoc(credit: number | null, collateral: number | null): number | null {
  if (credit == null || collateral == null || collateral <= 0) return null;
  return (credit / collateral) * 100;
}

function computeHeldRoc(
  position: OptionPosition,
  credit: number | null,
  collateral: number | null,
  openRoc: number | null,
): number | null {
  const profit = asNum(position.current_profit);
  if (credit != null && collateral != null && collateral > 0 && profit != null) {
    return ((credit + profit) / collateral) * 100;
  }
  const dte = asNum(position.dte);
  if (openRoc != null && dte != null && dte > 0) {
    return openRoc * (365 / dte);
  }
  return openRoc;
}

function resolvePop(
  position: OptionPosition,
  rec: WheelRecommendation | undefined,
  allowMarketdata: boolean,
): number | null {
  if (position.pop != null && Number.isFinite(position.pop)) return position.pop;
  if (rec?.pop != null && Number.isFinite(rec.pop)) return rec.pop;
  const sym = position.option_symbol;
  if (allowMarketdata && sym) {
    const cached = getCachedMarketdataOptionPop(sym);
    if (cached !== undefined) return cached;
  }
  return null;
}

function missingPopSymbols(
  positions: PositionsSnapshot,
  recLookup: Map<string, WheelRecommendation>,
): string[] {
  const missing: string[] = [];
  const seen = new Set<string>();
  for (const p of positions.options) {
    const rec = matchPositionToRecommendation(p, recLookup);
    if (rec?.pop != null && Number.isFinite(rec.pop)) continue;
    const sym = p.option_symbol?.trim().toUpperCase().replace(/[^A-Z0-9]/g, "") ?? "";
    if (!sym || seen.has(sym)) continue;
    if (getCachedMarketdataOptionPop(sym) !== undefined) continue;
    seen.add(sym);
    missing.push(sym);
  }
  return missing;
}

/** PoP is optional. Fetch it after the table is on screen so a slow batch cannot pin the loading shell. */
function fillMissingPopsInBackground(
  positions: PositionsSnapshot,
  recLookup: Map<string, WheelRecommendation>,
  generation: number,
  onFilled: () => void,
): void {
  const missing = missingPopSymbols(positions, recLookup);
  if (!missing.length) return;
  void (async () => {
    for (let i = 0; i < missing.length; i += 40) {
      if (generation !== panelGeneration) return;
      await fetchMarketdataOptionPopBatch(missing.slice(i, i + 40), false);
    }
    if (generation !== panelGeneration) return;
    onFilled();
  })().catch(() => {
    /* table already shows without PoP */
  });
}

function enrichRows(
  positions: PositionsSnapshot,
  recLookup: Map<string, WheelRecommendation>,
  stockAvgs: Map<string, number>,
  positionSource: PositionsFeedSource,
): EnrichedOptionRow[] {
  const stocks = stockByTicker(positions);
  const rows = positions.options.map((position) => {
    const ticker = normalizeTicker(position.ticker);
    const call = isCall(position);
    const rec = matchPositionToRecommendation(position, recLookup);
    const avgCost =
      resolveShareAvgCostForTicker(
        ticker,
        stockAvgs,
        rec,
        stocks.get(ticker),
        position.account_id,
      ) ?? null;
    const stockPrice = resolveRowStockPrice(position, ticker, positions, rec, positionSource);
    const pop = resolvePop(position, rec, true);
    const credit = optionPremiumReceived(position);
    const rocCollateral = optionRocCollateral(position, avgCost);
    const openRoc = computeOpenRoc(credit, rocCollateral);
    const heldRoc = computeHeldRoc(position, credit, rocCollateral, openRoc);
    const itm = optionIsItm(position, stockPrice);
    const moneyness = optionMoneyness(position, stockPrice);
    const risky = call && callStrikeVsAvgCost(position.strike, avgCost) === "below";
    return {
      position,
      ticker,
      sector: position.sector?.trim() || "Unknown",
      avgCost,
      stockPrice,
      isCall: call,
      isItm: itm,
      moneyness,
      isRisky: risky,
      pop,
      credit,
      openRoc,
      heldRoc,
      mark: optionLedgerMark(position),
    };
  });
  assignAtmBadges(rows);
  return rows;
}

function applyFilters(rows: EnrichedOptionRow[], state: OptionsFilterState): EnrichedOptionRow[] {
  const q = state.ticker.trim().toUpperCase();
  return rows.filter((row) => {
    const p = row.position;
    if (p.account_id) {
      if (!scopeAllowsAccount(p.account_id, p.broker_id)) return false;
    } else if (!scopeAllowsTicker(row.ticker)) {
      return false;
    }
    if (p.expired && state.expiration !== "expired" && state.expiration !== "all") {
      return false;
    }
    if (q && !row.ticker.startsWith(q)) return false;
    if (state.type === "call" && !row.isCall) return false;
    if (state.type === "put" && row.isCall) return false;
    if (state.moneyness === "itm" && !row.isItm) return false;
    if (state.moneyness === "otm") {
      if (row.isItm) return false;
      if (row.stockPrice == null || !Number.isFinite(row.stockPrice)) return false;
    }
    if (state.sector && row.sector !== state.sector) return false;
    if (state.expiration === "expired" && !p.expired) return false;
    if (state.expiration === "at-expiration") {
      const dte = asNum(p.dte);
      if (dte == null || dte < 0 || dte > 2) return false;
    }
    if (state.risk === "risky" && !row.isRisky) return false;
    if (state.expiration === "all" && p.expired) return false;
    return true;
  });
}

function activeRows(rows: EnrichedOptionRow[]): EnrichedOptionRow[] {
  return rows.filter((r) => !r.position.expired);
}

function fmtMoney(value: number | null | undefined, signed = false): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  const text = fmtUsd(abs);
  if (!signed || value === 0) return value < 0 ? `-${text}` : text;
  return value < 0 ? `-${text}` : text;
}

function fmtPct(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

function fmtPctSigned(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

function plClass(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value === 0) return "";
  return value > 0 ? "roi-strong" : "roi-loss";
}

function rocClass(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "";
  return value >= 12 ? "roi-strong" : value >= 0 ? "" : "roi-loss";
}

function computeSummary(rows: EnrichedOptionRow[]): {
  openLegs: number;
  contracts: number;
  openCredit: number;
  putCollateral: number;
  callCollateral: number;
  callRisk: number;
  callOpty: number;
  callItmAtmRisk: number;
  callRiskOptyNet: number;
  callRiskCredit: number;
  callOptyCredit: number;
  callItmAtmRiskCredit: number;
  totalPl: number;
  totalPlPct: number | null;
} {
  const active = activeRows(rows);
  let contracts = 0;
  let openCredit = 0;
  let putCollateral = 0;
  let callCollateral = 0;
  let callRisk = 0;
  let callOpty = 0;
  let callItmAtmRisk = 0;
  let callRiskCredit = 0;
  let callOptyCredit = 0;
  let callItmAtmRiskCredit = 0;
  let totalPl = 0;
  let collateralForPl = 0;

  for (const row of active) {
    const p = row.position;
    const c = Math.abs(Number(p.contracts) || 0);
    contracts += c;
    if (p.open_credit != null && Number.isFinite(p.open_credit)) {
      openCredit += Math.abs(p.open_credit);
    } else if (row.credit != null) {
      openCredit += row.credit;
    }
    const coll = optionStrikeCollateral(p) ?? 0;
    if (row.isCall) callCollateral += coll;
    else putCollateral += coll;
    if (row.isCall) {
      const exposure = callRiskOptyExposure("call", p.strike, row.avgCost, p.contracts);
      const credit = optionPremiumReceived(p) ?? row.credit ?? 0;
      if (exposure?.kind === "risk") {
        callRisk += exposure.amount;
        callRiskCredit += credit;
        if (row.moneyness === "itm" || row.moneyness === "atm") {
          callItmAtmRisk += exposure.amount;
          callItmAtmRiskCredit += credit;
        }
      } else if (exposure?.kind === "opty") {
        callOpty += exposure.amount;
        callOptyCredit += credit;
      }
    }
    const pl = asNum(p.current_profit);
    if (pl != null) totalPl += pl;
    if (pl != null && coll > 0) collateralForPl += coll;
  }

  const totalPlPct =
    collateralForPl > 0 && Number.isFinite(totalPl) ? (totalPl / collateralForPl) * 100 : null;

  return {
    openLegs: active.length,
    contracts,
    openCredit,
    putCollateral,
    callCollateral,
    callRisk,
    callOpty,
    callItmAtmRisk,
    callRiskOptyNet: callOpty - callRisk,
    callRiskCredit,
    callOptyCredit,
    callItmAtmRiskCredit,
    totalPl,
    totalPlPct,
  };
}

function buildDiversitySlices(
  rows: EnrichedOptionRow[],
  key: "sector" | "ticker",
): DiversitySlice[] {
  const totals = new Map<string, number>();
  let grand = 0;
  for (const row of activeRows(rows)) {
    const coll = optionStrikeCollateral(row.position) ?? 0;
    if (coll <= 0) continue;
    const label = key === "sector" ? row.sector : row.ticker;
    totals.set(label, (totals.get(label) ?? 0) + coll);
    grand += coll;
  }
  if (grand <= 0) return [];
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, value], i) => ({
      label,
      value,
      color: DONUT_COLORS[i % DONUT_COLORS.length],
      pct: (value / grand) * 100,
    }));
}

function renderDonut(slices: DiversitySlice[], centerTop: string, centerSub: string): string {
  if (!slices.length) {
    return `<p class="div-empty muted">No collateral to chart.</p>`;
  }
  const cx = 100;
  const cy = 100;
  const r = 70;
  const stroke = 26;
  const circumference = 2 * Math.PI * r;
  let offset = 0;
  const circles = slices
    .map((slice) => {
      const len = (slice.value / slices.reduce((s, x) => s + x.value, 0)) * circumference;
      const dash = `${len} ${circumference - len}`;
      const circle = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${slice.color}" stroke-width="${stroke}" stroke-dasharray="${dash}" stroke-dashoffset="${-offset}" transform="rotate(-90 ${cx} ${cy})"></circle>`;
      offset += len;
      return circle;
    })
    .join("");
  return `
    <div class="donut-wrap">
      <svg class="donut" viewBox="0 0 200 200" role="img" aria-label="${escAttr(centerSub)}">
        ${circles}
        <text class="donut-center-top" x="${cx}" y="${cy - 2}" text-anchor="middle">${esc(centerTop)}</text>
        <text class="donut-center-sub" x="${cx}" y="${cy + 14}" text-anchor="middle">${esc(centerSub)}</text>
      </svg>
    </div>`;
}

function renderLegend(slices: DiversitySlice[], compact = false): string {
  if (!slices.length) return "";
  const cls = compact ? "div-legend div-legend-cols" : "div-legend";
  const items = slices
    .map(
      (slice) =>
        `<li><span class="sw" style="background:${slice.color}"></span><span class="nm">${esc(slice.label)}</span>${
          compact ? "" : `<span class="vl">${fmtMoney(slice.value)}</span>`
        }<span class="pc">${slice.pct.toFixed(1)}%</span></li>`,
    )
    .join("");
  return `<ul class="${cls}">${items}</ul>`;
}

function sectorOptions(rows: EnrichedOptionRow[], selected: string): string {
  const sectors = new Set<string>();
  for (const row of activeRows(rows)) sectors.add(row.sector);
  const opts = [`<option value="">All sectors</option>`];
  for (const sector of [...sectors].sort()) {
    const sel = sector === selected ? " selected" : "";
    opts.push(`<option value="${escAttr(sector)}"${sel}>${esc(sector)}</option>`);
  }
  return opts.join("");
}

function kpiHint(tip: string): string {
  return `<span class="hint" tabindex="0" role="img" data-tip="${escAttr(tip)}">i</span>`;
}

function callRiskOptySubHtml(summary: ReturnType<typeof computeSummary>): string {
  const riskAmt = summary.callRisk > 0 ? -summary.callRisk : 0;
  const optyAmt = summary.callOpty;
  const itmAtmRiskAmt = summary.callItmAtmRisk > 0 ? -summary.callItmAtmRisk : 0;
  const line = (
    label: string,
    exposure: number,
    credit: number,
    tone: "roi-loss" | "roi-strong",
  ): string =>
    `<span class="sfe-call-risk-opty-line">
      <span class="sfe-call-risk-opty-left ${tone}">${label} ${fmtUsdWhole(exposure)}</span>
      <span class="sfe-call-risk-opty-right">credit ${fmtUsdWhole(credit)}</span>
    </span>`;
  return `<span class="kpi-sub sfe-call-risk-opty-sub">${line("risk", riskAmt, summary.callRiskCredit, "roi-loss")}${line("opty", optyAmt, summary.callOptyCredit, "roi-strong")}${line("ITM/ATM", itmAtmRiskAmt, summary.callItmAtmRiskCredit, "roi-loss")}</span>`;
}

function kpiCard(
  label: string,
  value: string,
  valueCls = "",
  sub = "",
  tip = "",
  subCls = "",
  subIsHtml = false,
  cardCls = "",
): string {
  const hint = tip ? kpiHint(tip) : "";
  const subHtml = sub
    ? subIsHtml
      ? sub
      : `<span class="kpi-sub${subCls ? ` ${subCls}` : ""}">${sub}</span>`
    : "";
  return `<div class="kpi${cardCls ? ` ${cardCls}` : ""}"><span class="kpi-k">${esc(label)}${hint}</span><span class="kpi-v ${valueCls}">${value}</span>${subHtml}</div>`;
}

function renderKpiRow(summary: ReturnType<typeof computeSummary>): string {
  const plCls = plClass(summary.totalPl);
  return `
    <div class="kpi-row">
      ${kpiCard(
        "Open option legs",
        String(summary.openLegs),
        "",
        "",
        "Number of distinct open short option contracts (each strike/expiry counts once).",
      )}
      ${kpiCard(
        "Contracts",
        String(summary.contracts),
        "",
        "",
        "Total open contracts across all legs (net of any buy-to-close).",
      )}
      ${kpiCard(
        "Open credit",
        fmtMoney(summary.openCredit),
        plClass(summary.openCredit),
        "",
        "Net premium you collected opening these legs and still hold (before closing costs).",
      )}
      ${kpiCard(
        "Put collateral",
        fmtMoney(summary.putCollateral),
        "",
        "cash secured",
        "Cash-secured put collateral = strike × 100 × contracts, summed over open puts. This is the cash you must reserve to buy shares if assigned.",
      )}
      ${kpiCard(
        "Call Risk/Opty",
        fmtUsdWhole(summary.callRiskOptyNet),
        plClass(summary.callRiskOptyNet),
        callRiskOptySubHtml(summary),
        "Net covered-call assignment exposure vs share avg cost: opportunity (strike above avg) minus risk (strike below avg). ITM/ATM is the risky subset where stock is at or through the strike. Each line shows exposure on the left and open credit collected on the right.",
        "",
        true,
        "sfe-kpi-call-risk-opty",
      )}
      ${kpiCard(
        "Total P/L",
        fmtMoney(summary.totalPl, true),
        plCls,
        fmtPctSigned(summary.totalPlPct),
        "Unrealized profit/loss on your open option legs: premium collected minus the cost to close at current mid (priced legs only). The % is P/L against premium collected.",
        plCls,
      )}
    </div>`;
}

function renderFilterBar(
  state: OptionsFilterState,
  rows: EnrichedOptionRow[],
  _cache: OptionsPanelCache,
): string {
  return `
    <div class="filter-bar recs-filter-bar">
      <div class="filter-field">
        <label for="wheelOptTicker">Ticker</label>
        <input type="text" id="wheelOptTicker" class="filter-input-compact" placeholder="All"${TICKER_FILTER_INPUT_ATTRS} value="${escAttr(state.ticker)}" />
      </div>
      <div class="filter-field">
        <label>Type</label>
        <div class="year-chips" id="wheelOptTypeFilter" role="group">
          <button type="button" class="year-chip${state.type === "all" ? " active" : ""}" data-opt-type="all">All</button>
          <button type="button" class="year-chip${state.type === "put" ? " active" : ""}" data-opt-type="put">Puts</button>
          <button type="button" class="year-chip${state.type === "call" ? " active" : ""}" data-opt-type="call">Calls</button>
        </div>
      </div>
      <div class="filter-field">
        <label>Moneyness</label>
        <div class="year-chips" id="wheelOptMnyFilter" role="group">
          <button type="button" class="year-chip${state.moneyness === "all" ? " active" : ""}" data-opt-mny="all">All</button>
          <button type="button" class="year-chip${state.moneyness === "itm" ? " active" : ""}" data-opt-mny="itm">ITM</button>
          <button type="button" class="year-chip${state.moneyness === "otm" ? " active" : ""}" data-opt-mny="otm">OTM</button>
        </div>
      </div>
      <div class="filter-field">
        <label for="wheelOptSector">Sector</label>
        <select id="wheelOptSector" aria-label="Sector filter">${sectorOptions(rows, state.sector)}</select>
      </div>
      <div class="filter-field">
        <label for="wheelOptExpiration">Expiration</label>
        <select id="wheelOptExpiration" class="sfe-options-expiration-select" aria-label="Expiration filter">
          <option value="all"${state.expiration === "all" ? " selected" : ""}>All</option>
          <option value="expired"${state.expiration === "expired" ? " selected" : ""}>Expired</option>
          <option value="at-expiration"${state.expiration === "at-expiration" ? " selected" : ""}>At expiration</option>
        </select>
      </div>
      <div class="filter-field">
        <label>Risk</label>
        <div class="year-chips" id="wheelOptRiskFilter" role="group">
          <button type="button" class="year-chip${state.risk === "all" ? " active" : ""}" data-opt-risk="all">All</button>
          <button type="button" class="year-chip${state.risk === "risky" ? " active" : ""}" data-opt-risk="risky">Risky</button>
        </div>
      </div>
      ${advisoryFilterFieldHtml()}
      <div class="filter-field">
        <div class="sfe-filter-reset-actions">
          <button type="button" class="small sfe-reset-filters" id="wheelOptResetFilters" aria-label="Reset filters" title="Reset filters"><svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h11l-4.2 5.2V16l-2.6 1.4V10.2L3 5z"/><path d="m15 15 6 6"/><path d="m21 15-6 6"/></svg></button>
          ${screenerAdviceButtonHtml()}
        </div>
      </div>
    </div>`;
}

function renderOptionsTickerCell(row: EnrichedOptionRow, compact: boolean): string {
  const sideCls = row.isCall ? "side-call" : "side-put";
  const sideLabel = row.isCall ? "Call" : "Put";
  const sideBadge = `<span class="side-badge ${sideCls}">${sideLabel}</span>`;
  const mnyBadge = moneynessBadgeHtml(row.moneyness);
  const cellCls = compact ? "tk-cell sfe-web-compact-cell" : "tk-cell";
  return `<td class="${cellCls}" data-col="ticker"><div class="sfe-options-ticker-cell">${mnyBadge}<div class="sfe-options-ticker-stack"><span class="recs-ticker" data-ticker="${escAttr(row.ticker)}" title="Open ${escAttr(row.ticker)} on Stock Forge">${esc(row.ticker)}<span class="tk-ic">↗</span></span>${sideBadge}</div></div></td>`;
}

function strikePopMarkup(row: EnrichedOptionRow, compact: boolean): string {
  if (row.pop == null || !Number.isFinite(row.pop)) return "";
  if (compact) return compactSubline(`POP: ${fmtWheelRatio(row.pop)}`, "sfe-web-compact-sub");
  return `<small class="sfe-strike-pop">POP: ${fmtWheelRatio(row.pop)}</small>`;
}

/** Own-ledger open dates arrive as YYYY-MM-DD, sometimes with a time suffix. */
function openDateLabel(value: string | null | undefined): string {
  const iso = String(value ?? "").trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return iso ? iso[1] : "—";
}

function openDateIso(value: string | null | undefined): string | null {
  const iso = String(value ?? "").trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return iso?.[1] ?? null;
}

function daysOpen(value: string | null | undefined): number | null {
  const iso = openDateIso(value);
  if (!iso) return null;
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return null;
  const opened = Date.UTC(year, month - 1, day);
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((today - opened) / 86_400_000);
  return Number.isFinite(days) && days >= 0 ? days : null;
}

function openDateCellInner(value: string | null | undefined): string {
  const date = openDateLabel(value);
  if (date === "—") return "—";
  const days = daysOpen(value);
  const held = days == null ? "" : compactSubline(`open ${days}d`, "sfe-web-compact-sub");
  return `${esc(date)}${held}`;
}

function renderOptionCell(colId: string, row: EnrichedOptionRow): string {
  const p = row.position;
  const compact = isWebCompactMode();
  const strikeTip = row.isCall ? callStrikeVsAvgTip(p.strike, row.avgCost) : "";
  const strikeTipAttr = strikeTip ? ` data-tip="${escAttr(strikeTip)}"` : "";
  const popLine = strikePopMarkup(row, compact);
  const pl = asNum(p.current_profit);
  const plPct = asNum(p.current_profit_pct);
  const collateralAmount = optionStrikeCollateral(p);
  const collateralDisplay = formatCollateralDisplay(row.isCall, collateralAmount);

  switch (colId) {
    case "ticker":
      return renderOptionsTickerCell(row, compact);
    case "broker_mark":
      return brokerMarkCellHtml(row.mark);
    case "account_short":
      return accountShortCellHtml(row.mark);
    case "sector":
      return `<td class="sector-cell" data-col="sector">${esc(row.sector)}</td>`;
    case "sfe_holding_avg_cost":
      return `<td class="num sfe-avg-cost-cell" data-col="sfe_holding_avg_cost">${row.isCall ? fmtUsd(row.avgCost) : "—"}</td>`;
    case "strike":
      return `<td class="num sfe-web-compact-cell${strikeTip ? " sfe-data-tip-host" : ""}" data-col="strike"${strikeTipAttr}>${fmtUsd(p.strike)}${popLine}</td>`;
    case "sfe_risk_opty": {
      const exposure = callRiskOptyExposure(
        row.isCall ? "call" : "put",
        p.strike,
        row.avgCost,
        p.contracts,
      );
      if (!exposure) {
        return `<td class="num sfe-risk-opty-cell muted" data-col="sfe_risk_opty">—</td>`;
      }
      const tip =
        exposure.kind === "risk"
          ? "Assignment risk vs share avg cost: (avg cost − strike) × shares."
          : "Upside vs share avg cost if assigned: (strike − avg cost) × shares.";
      const signed = exposure.kind === "risk" ? -exposure.amount : exposure.amount;
      return `<td class="num sfe-risk-opty-cell ${callRiskOptyValueClass(exposure.kind)} sfe-data-tip-host" data-col="sfe_risk_opty" data-tip="${escAttr(tip)}">${fmtUsdWhole(signed)}</td>`;
    }
    case "price": {
      if (compact) {
        const avg = row.isCall ? fmtUsd(row.avgCost) : "—";
        return `<td class="num sfe-web-compact-cell" data-col="price">${fmtUsd(row.stockPrice)}${compactSubline(row.isCall ? `avg ${avg}` : "—", "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="price">${fmtUsd(row.stockPrice)}</td>`;
    }
    case "target":
      return `<td class="num" data-col="target">${fmtUsd(p.target_price)}</td>`;
    case "open_date": {
      const cls = compact ? "sfe-web-compact-cell sfe-options-open-cell" : "sfe-options-open-cell";
      return `<td class="${cls}" data-col="open_date">${openDateCellInner(p.open_date)}</td>`;
    }
    case "expiration": {
      const inner = renderScreenerExpiryInnerHtml(
        { expiration_date: p.exp_date, dte: p.dte != null ? Number(p.dte) : null },
        esc,
      );
      const cls = compact ? "sfe-web-compact-cell" : "sfe-screener-expiry-cell";
      const expDate = screenerExpiryDateLabel(p.exp_date);
      const dateAttr = expDate !== "—" ? ` data-expiration-date="${escAttr(expDate)}"` : "";
      return `<td class="${cls}" data-col="expiration"${dateAttr}>${inner}</td>`;
    }
    case "dte":
      return `<td class="num" data-col="dte">${p.dte != null ? p.dte : "—"}</td>`;
    case "qty":
      return `<td class="num" data-col="qty">${Math.abs(Number(p.contracts) || 0)}</td>`;
    case "entry": {
      if (compact) {
        return `<td class="num sfe-web-compact-cell" data-col="entry">${fmtUsd(p.entry_price)}${compactSubline(`mid ${fmtUsd(p.current_price)}`, "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num" data-col="entry">${fmtUsd(p.entry_price)}</td>`;
    }
    case "credit": {
      const creditCls = plClass(row.credit);
      if (compact) {
        return `<td class="num sfe-web-compact-cell ${creditCls}" data-col="credit">${fmtUsd(row.credit)}${compactSubline(collateralSublineMarkup(row.isCall, collateralAmount), "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num ${creditCls}" data-col="credit">${fmtUsd(row.credit)}</td>`;
    }
    case "mark":
      return `<td class="num" data-col="mark">${fmtUsd(p.current_price)}</td>`;
    case "pnl": {
      const btcDot = renderBtcEarlyCloseDotFrom({
        capturedPct: plPct,
        dte: asNum(p.dte),
        mark: asNum(p.current_price),
        moneyness: row.moneyness,
        stockPrice: row.stockPrice,
        strike: p.strike,
        contracts: p.contracts,
        avgCost: row.avgCost,
        isCall: row.isCall,
        expired: p.expired,
        openDate: p.open_date,
        expDate: p.exp_date,
      });
      if (compact) {
        return `<td class="num pos-pl-cell sfe-web-compact-cell ${plClass(pl)}" data-col="pnl"><span class="pl-amt">${fmtMoney(pl, true)}${btcDot}</span>${compactSubline(fmtPctSigned(plPct), "sfe-web-compact-sub")}</td>`;
      }
      return `<td class="num pos-pl-cell ${plClass(pl)}" data-col="pnl"><span class="pl-amt">${fmtMoney(pl, true)}${btcDot}</span><span class="pl-pct">${fmtPctSigned(plPct)}</span></td>`;
    }
    case "held_roc":
      return `<td class="num ${rocClass(row.heldRoc)}" data-col="held_roc">${fmtPct(row.heldRoc)}</td>`;
    case "open_roc":
      return `<td class="num ${rocClass(row.openRoc)}" data-col="open_roc">${fmtPct(row.openRoc)}</td>`;
    case "collateral":
      return `<td class="num${row.isCall ? " sfe-call-collateral-na" : ""}" data-col="collateral">${collateralDisplay}</td>`;
    case "actions":
      return `<td class="pos-actions" data-col="actions">${renderFocusPositionRowMenu(p)}</td>`;
    default:
      return `<td data-col="${colId}">—</td>`;
  }
}

interface OptionsSortState {
  key: string | null;
  direction: "asc" | "desc" | null;
}

function loadOptionsSort(): OptionsSortState {
  try {
    const raw = localStorage.getItem(SORT_KEY);
    if (!raw) return { key: null, direction: null };
    const parsed = JSON.parse(raw) as { key?: unknown; direction?: unknown };
    if (
      typeof parsed.key !== "string" ||
      !parsed.key ||
      (parsed.direction !== "asc" && parsed.direction !== "desc")
    ) {
      return { key: null, direction: null };
    }
    return { key: parsed.key, direction: parsed.direction };
  } catch {
    return { key: null, direction: null };
  }
}

let optionsSort: OptionsSortState = loadOptionsSort();

function saveOptionsSort(): void {
  localStorage.setItem(SORT_KEY, JSON.stringify(optionsSort));
}

function cycleOptionsSort(key: string): void {
  if (optionsSort.key !== key || !optionsSort.direction) {
    optionsSort = { key, direction: "asc" };
  } else if (optionsSort.direction === "asc") {
    optionsSort = { key, direction: "desc" };
  } else {
    optionsSort = { key: null, direction: null };
  }
  saveOptionsSort();
}

function isEmptySortValue(value: unknown): boolean {
  return (
    value == null ||
    value === "" ||
    (typeof value === "number" && !Number.isFinite(value))
  );
}

function compareSortValues(left: unknown, right: unknown, direction: "asc" | "desc"): number {
  const leftEmpty = isEmptySortValue(left);
  const rightEmpty = isEmptySortValue(right);
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;
  let cmp = 0;
  if (typeof left === "number" && typeof right === "number") cmp = left - right;
  else {
    cmp = String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }
  return direction === "asc" ? cmp : -cmp;
}

function sortOptionsRows(rows: EnrichedOptionRow[]): EnrichedOptionRow[] {
  const key = optionsSort.key;
  const direction = optionsSort.direction;
  if (!key || !direction) return rows;
  return [...rows].sort((left, right) => {
    const leftValue = key === "qty" ? Math.abs(Number(left.position.contracts) || 0) : optionColumnValue(left, key);
    const rightValue =
      key === "qty" ? Math.abs(Number(right.position.contracts) || 0) : optionColumnValue(right, key);
    const cmp = compareSortValues(leftValue, rightValue, direction);
    if (cmp !== 0) return cmp;
    const ticker = left.ticker.localeCompare(right.ticker);
    if (ticker !== 0) return ticker;
    return String(left.position.option_symbol ?? "").localeCompare(String(right.position.option_symbol ?? ""));
  });
}

function paintOptionsSortHeaders(root: HTMLElement): void {
  const headers = root.querySelectorAll<HTMLTableCellElement>(
    "#wheelPositionsTable thead th[data-col], table[data-sfe-float-for='wheelPositionsTable'] thead th[data-col]",
  );
  for (const th of headers) {
    th.querySelector(".sort-ind")?.remove();
    const key = th.dataset.col ?? "";
    if (!key || key === "actions") continue;
    if (!th.hasAttribute("data-sort")) th.setAttribute("data-sort", key);
    const active = optionsSort.key === key && optionsSort.direction != null;
    th.setAttribute("aria-sort", active ? (optionsSort.direction === "asc" ? "ascending" : "descending") : "none");
    if (!active || !optionsSort.direction) continue;
    attachSortIndicator(th, optionsSort.direction);
  }
}

let optionsSortRerender: (() => void) | null = null;

function bindOptionsHeaderSort(root: HTMLElement, rerender: () => void): void {
  optionsSortRerender = rerender;
  if (document.documentElement.dataset.sfeOptionsSort !== "1") {
    document.documentElement.dataset.sfeOptionsSort = "1";
    document.addEventListener("click", (event) => {
      const rerenderSort = optionsSortRerender;
      if (!rerenderSort) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const th = target.closest("th[data-col]");
      if (!(th instanceof HTMLTableCellElement)) return;
      const table = th.closest("table");
      const isOptionsTable =
        table?.id === "wheelPositionsTable" || table?.dataset.sfeFloatFor === "wheelPositionsTable";
      if (!isOptionsTable) return;
      const key = th.dataset.col ?? "";
      if (!key || key === "actions") return;
      event.preventDefault();
      cycleOptionsSort(key);
      rerenderSort();
    });
  }
  paintOptionsSortHeaders(root);
}

function optionColumnValue(row: EnrichedOptionRow, key: string): unknown {
  const p = row.position;
  switch (key) {
    case "ticker":
      return row.ticker;
    case "broker_mark":
      return row.mark?.brokerCode || null;
    case "account_short":
      return row.mark?.accountShort || null;
    case "sector":
      return row.sector;
    case "sfe_holding_avg_cost":
      return row.isCall ? row.avgCost : null;
    case "strike":
      return p.strike;
    case "sfe_risk_opty": {
      const exposure = callRiskOptyExposure(
        row.isCall ? "call" : "put",
        p.strike,
        row.avgCost,
        p.contracts,
      );
      if (!exposure) return null;
      return exposure.kind === "risk" ? -exposure.amount : exposure.amount;
    }
    case "price":
      return row.stockPrice;
    case "target":
      return p.target_price;
    case "open_date":
      return p.open_date ?? null;
    case "expiration":
      return p.exp_date;
    case "dte":
      return p.dte;
    case "qty":
      return p.contracts;
    case "entry":
      return p.entry_price;
    case "credit":
      return row.credit;
    case "mark":
      return p.current_price;
    case "pnl":
      return asNum(p.current_profit);
    case "held_roc":
      return row.heldRoc;
    case "open_roc":
      return row.openRoc;
    case "collateral":
      return row.isCall ? null : optionStrikeCollateral(p);
    case "actions":
      return true;
    default:
      return null;
  }
}

function visibleColumnsForRows(
  columns: ColumnState[],
  rows: EnrichedOptionRow[],
): ColumnState[] {
  return displayTableColumnsForData(columns, isWebCompactMode(), rows, optionColumnValue).filter(
    (column) => column.key !== "dte",
  );
}

function renderTable(rows: EnrichedOptionRow[], columns: ColumnState[]): string {
  const visible = visibleColumnsForRows(columns, rows);
  if (!rows.length) {
    return `<tr><td colspan="${Math.max(visible.length, 1)}" class="muted">No open option positions match your filters.</td></tr>`;
  }
  return rows
    .map((row) => {
      const sym = escAttr(row.position.option_symbol ?? "");
      const cells = visible.map((column) => renderOptionCell(column.key, row)).join("");
      return `<tr data-symbol="${sym}" ${adviceRowAttrs({
        underlying: row.ticker,
        side: row.isCall ? "call" : "put",
        strike: row.position.strike,
        expiration: row.position.exp_date,
        optionSymbol: row.position.option_symbol,
      })}>${cells}</tr>`;
    })
    .join("");
}

function rowSymbolKey(row: EnrichedOptionRow): string {
  return row.position.option_symbol ?? "";
}

function morphTableCell(td: HTMLTableCellElement, nextHtml: string): boolean {
  const tpl = document.createElement("template");
  tpl.innerHTML = nextHtml.trim();
  const next = tpl.content.firstElementChild;
  if (!(next instanceof HTMLTableCellElement)) return false;
  if (td.outerHTML === next.outerHTML) return false;

  for (const attr of [...td.attributes]) {
    if (!next.hasAttribute(attr.name)) td.removeAttribute(attr.name);
  }
  for (const attr of next.attributes) {
    td.setAttribute(attr.name, attr.value);
  }
  td.innerHTML = next.innerHTML;
  return true;
}

function patchOptionRow(
  tr: HTMLTableRowElement,
  row: EnrichedOptionRow,
  visible: ColumnState[],
): void {
  for (const column of visible) {
    if (column.key === "actions") continue;
    const td = tr.querySelector<HTMLTableCellElement>(`td[data-col="${column.key}"]`);
    if (!td) continue;
    morphTableCell(td, renderOptionCell(column.key, row));
  }
}

function patchOptionsTableBody(
  tbody: HTMLElement,
  rows: EnrichedOptionRow[],
  columns: ColumnState[],
): boolean {
  const visible = visibleColumnsForRows(columns, rows);
  if (!rows.length) {
    const onlyRow = tbody.querySelector("tr");
    const emptyHtml = `<tr><td colspan="${Math.max(visible.length, 1)}" class="muted">No open option positions match your filters.</td></tr>`;
    if (onlyRow?.querySelector("td.muted") && !onlyRow.dataset.symbol) {
      return true;
    }
    tbody.innerHTML = emptyHtml;
    return true;
  }

  const existingRows = [...tbody.querySelectorAll<HTMLTableRowElement>("tr[data-symbol]")];
  const canPatchInline =
    existingRows.length === rows.length &&
    existingRows.every((tr, index) => (tr.dataset.symbol ?? "") === rowSymbolKey(rows[index]!));

  if (!canPatchInline) return false;

  existingRows.forEach((tr, index) => {
    patchOptionRow(tr, rows[index]!, visible);
  });
  return true;
}

function patchKpiRow(kpiRow: HTMLElement, summary: ReturnType<typeof computeSummary>): void {
  const cards = [...kpiRow.querySelectorAll<HTMLElement>(".kpi")];
  const plCls = plClass(summary.totalPl);
  const patches: Array<{ value: string; valueCls?: string; sub?: string; subCls?: string; subHtml?: string }> = [
    { value: String(summary.openLegs) },
    { value: String(summary.contracts) },
    { value: fmtMoney(summary.openCredit), valueCls: plClass(summary.openCredit) },
    { value: fmtMoney(summary.putCollateral), sub: "cash secured" },
    {
      value: fmtUsdWhole(summary.callRiskOptyNet),
      valueCls: plClass(summary.callRiskOptyNet),
      subHtml: callRiskOptySubHtml(summary),
    },
    {
      value: fmtMoney(summary.totalPl, true),
      valueCls: plCls,
      sub: fmtPctSigned(summary.totalPlPct),
      subCls: plCls,
    },
  ];

  cards.forEach((card, index) => {
    const patch = patches[index];
    if (!patch) return;
    const valueEl = card.querySelector<HTMLElement>(".kpi-v");
    if (valueEl) {
      const valueChanged = valueEl.textContent !== patch.value;
      if (valueChanged) valueEl.textContent = patch.value;
      const nextCls = `kpi-v${patch.valueCls ? ` ${patch.valueCls}` : ""}`.trim();
      if (valueEl.className !== nextCls) valueEl.className = nextCls;
    }
    const subEl = card.querySelector<HTMLElement>(".kpi-sub");
    if (patch.subHtml) {
      if (subEl) {
        if (subEl.innerHTML !== patch.subHtml) subEl.innerHTML = patch.subHtml;
      } else {
        card.insertAdjacentHTML("beforeend", patch.subHtml);
      }
      return;
    }
    if (patch.sub) {
      if (subEl) {
        if (subEl.textContent !== patch.sub) subEl.textContent = patch.sub;
        const nextSubCls = `kpi-sub${patch.subCls ? ` ${patch.subCls}` : ""}`.trim();
        if (subEl.className !== nextSubCls) subEl.className = nextSubCls;
      } else {
        card.insertAdjacentHTML(
          "beforeend",
          `<span class="kpi-sub${patch.subCls ? ` ${patch.subCls}` : ""}">${patch.sub}</span>`,
        );
      }
    }
  });
}

function markSfEnrichedHeader(headerHtml: string): string {
  if (/sfe-col-sf-enrich/.test(headerHtml)) return headerHtml;
  return headerHtml.replace(/<th\b([^>]*)>/i, (_match, attrs: string) => {
    const titled = /title=/.test(attrs)
      ? attrs
      : `${attrs} title="Enriched from Stock Forge when Robinhood mid is unavailable"`;
    if (/class="([^"]*)"/.test(titled)) {
      return `<th${titled.replace(/class="([^"]*)"/, (_m, cls: string) =>
        cls.includes("sfe-col-sf-enrich") ? `class="${cls}"` : `class="${cls} sfe-col-sf-enrich"`,
      )}>`;
    }
    return `<th class="sfe-col-sf-enrich"${titled}>`;
  });
}

function renderOptionsTableHeadRow(columns: ColumnState[]): string {
  const marked = columns.map((column) => {
    if (!SF_ENRICHED_COL_KEYS.has(column.key) || !column.headerHtml) return column;
    return { ...column, headerHtml: markSfEnrichedHeader(column.headerHtml) };
  });
  return renderTableHeadRow(marked);
}

function renderPanelShell(
  state: OptionsFilterState,
  cache: OptionsPanelCache,
  filtered: EnrichedOptionRow[],
  columns: ColumnState[],
  stale = false,
): string {
  const summary = computeSummary(cache.rows);
  const sectorSlices = buildDiversitySlices(cache.rows, "sector");
  const tickerSlices = buildDiversitySlices(cache.rows, "ticker");
  const totalCollateral = sectorSlices.reduce((s, x) => s + x.value, 0);
  const centerTop = totalCollateral >= 1000 ? `$${(totalCollateral / 1000).toFixed(1)}k` : fmtMoney(totalCollateral);
  const staleNote = stale ? " · showing cached data, refreshing…" : "";
  const sourceNote =
    cache.positionSource === "own_ledger"
      ? " Open contracts from own ledger; mids &amp; sector from Robinhood when available. Orange-dot columns are enriched from Stock Forge."
      : cache.positionSource === "robinhood"
        ? " Market is closed — positions from Robinhood; sector and avg cost still use StockForge+ where available."
        : "";
  const visibleColumns = visibleColumnsForRows(columns, filtered);

  return `
    <div data-wheel-view="options" class="panel sfe-web-native-options" style="margin-top:12px;">
      <details class="collapsible" data-sfe-collapsible="positions"${collapsibleOpenAttr("web-options", "positions")}>
        <summary><h2 class="insight-h">Options · open positions &amp; performance</h2></summary>
        ${renderKpiRow(summary)}
        <p class="muted small">Figures use your latest positions snapshot. Quotes and mids may be cached briefly.${sourceNote}${staleNote}</p>
      </details>

      <details class="collapsible" data-sfe-collapsible="diversification"${collapsibleOpenAttr("web-options", "diversification")}>
        <summary><h2 class="insight-h">Diversification</h2></summary>
        <div class="diversity-grid">
          <div class="diversity-card">
            <h5>By sector</h5>
            ${renderDonut(sectorSlices, centerTop, "collateral")}
            ${renderLegend(sectorSlices)}
          </div>
          <div class="diversity-card">
            <h5>By ticker</h5>
            ${renderDonut(tickerSlices, centerTop, `${tickerSlices.length} tickers`)}
            ${renderLegend(tickerSlices, true)}
          </div>
        </div>
      </details>

      <div class="section-head" style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;">
        <h2 class="insight-h" style="margin:0;">Open option positions</h2>
      </div>
      ${renderFilterBar(state, cache.rows, cache)}
      <p class="muted small recs-filter-note">${filtered.length} open legs · put collateral ${fmtMoney(summary.putCollateral)} (cash) · call collateral ${fmtMoney(summary.callCollateral)} (shares)</p>
      <div class="recs-wrap">
        <div class="recs-hscroll">
          <table class="recs recs-compact recs-sticky-col sfe-wheel-table-options" id="wheelPositionsTable"${renderTablePinnedAttr(visibleColumns)}>
            ${renderTableColgroup(visibleColumns)}
            <thead>
              <tr>${renderOptionsTableHeadRow(visibleColumns)}</tr>
            </thead>
            <tbody id="wheelPositionsBody">${renderTable(filtered, columns)}</tbody>
          </table>
        </div>
      </div>
    </div>`;
}

function bindFilters(root: HTMLElement, state: OptionsFilterState, rerender: () => void): void {
  root.querySelector("#wheelOptTypeFilter")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-opt-type]");
    if (!btn?.dataset.optType) return;
    state.type = btn.dataset.optType as OptionsFilterState["type"];
    saveFilters(state);
    rerender();
  });

  root.querySelector("#wheelOptMnyFilter")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-opt-mny]");
    if (!btn?.dataset.optMny) return;
    state.moneyness = btn.dataset.optMny as OptionsFilterState["moneyness"];
    saveFilters(state);
    rerender();
  });

  root.querySelector("#wheelOptRiskFilter")?.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-opt-risk]");
    if (!btn?.dataset.optRisk) return;
    state.risk = btn.dataset.optRisk as OptionsFilterState["risk"];
    saveFilters(state);
    rerender();
  });

  root.querySelector<HTMLInputElement>("#wheelOptTicker")?.addEventListener("input", (event) => {
    state.ticker = sanitizeTickerFilterValue((event.target as HTMLInputElement).value).toUpperCase();
    saveFilters(state);
    rerender();
  });

  guardTickerFilterInputs(root);

  root.querySelector<HTMLSelectElement>("#wheelOptSector")?.addEventListener("change", (event) => {
    state.sector = (event.target as HTMLSelectElement).value;
    saveFilters(state);
    rerender();
  });

  root.querySelector<HTMLSelectElement>("#wheelOptExpiration")?.addEventListener("change", (event) => {
    const value = (event.target as HTMLSelectElement).value;
    state.expiration =
      value === "expired" || value === "at-expiration" ? value : "all";
    saveFilters(state);
    rerender();
  });

  root.querySelector("#wheelOptResetFilters")?.addEventListener("click", () => {
    Object.assign(state, defaultFilters());
    optionsSort = { key: null, direction: null };
    saveOptionsSort();
    saveFilters(state);
    rerender();
  });
}

async function resolvePositionsSnapshot(force: boolean): Promise<OptionsPositionsResolution> {
  return resolveOptionsPositionsSnapshot(loadPositionsSnapshot, force);
}

function panelCacheNeedsSessionRefresh(cache: OptionsPanelCache | null): boolean {
  if (!cache) return false;
  return sessionFeedSourceMismatch(cache.positionSource);
}

async function loadPanelCache(force: boolean): Promise<OptionsPanelCache> {
  const sessionChanged = panelCacheNeedsSessionRefresh(panelCache);
  if (
    !force &&
    !sessionChanged &&
    panelCache &&
    Date.now() - panelCache.fetchedAt < CACHE_MS &&
    !optionsCacheNeedsStockAvgs(panelCache)
  ) {
    return panelCache;
  }
  if (panelInflight && !force && !sessionChanged) return panelInflight;

  const load = async (): Promise<OptionsPanelCache> => {
    const generation = ++panelGeneration;
    try {
      const [{ positions, source, continued }, recs, stockAvgs] = await Promise.all([
        resolvePositionsSnapshot(force),
        loadRecommendationsSnapshot(force),
        buildOptionsStockAvgs(force),
      ]);
      const recLookup = buildRecommendationLookup(recs);
      const cache: OptionsPanelCache = {
        positions,
        recLookup,
        stockAvgs,
        rows: enrichRows(positions, recLookup, stockAvgs, source),
        fetchedAt: Date.now(),
        positionSource: source,
      };
      panelCache = cache;
      notifyHeaderFeedQuoteRefreshed(cache.fetchedAt);
      const republish = (nextPositions: PositionsSnapshot, nextSource: PositionsFeedSource): void => {
        if (generation !== panelGeneration) return;
        cache.positions = nextPositions;
        cache.positionSource = nextSource;
        cache.rows = enrichRows(nextPositions, recLookup, stockAvgs, nextSource);
        panelCache = cache;
        if (mountedPanel && panelHasDisplayedContent(mountedPanel)) {
          softUpdatePanel(mountedPanel, loadFilters(), cache);
        }
      };
      fillMissingPopsInBackground(positions, recLookup, generation, () => {
        republish(cache.positions, cache.positionSource);
      });
      void continued
        .then((next) => {
          if (generation !== panelGeneration) return;
          if (next.positions === positions) return;
          republish(next.positions, next.source);
          fillMissingPopsInBackground(next.positions, recLookup, generation, () => {
            republish(cache.positions, cache.positionSource);
          });
        })
        .catch(() => {
          /* first paint already has the ledger book */
        });
      return cache;
    } catch (err) {
      if (panelCache) return panelCache;
      throw err;
    } finally {
      panelInflight = null;
    }
  };

  panelInflight = load();
  return panelInflight;
}

function panelHasDisplayedContent(panel: HTMLElement): boolean {
  return panel.querySelector(".sfe-web-native-options") != null;
}

function softUpdatePanel(
  panel: HTMLElement,
  state: OptionsFilterState,
  cache: OptionsPanelCache,
): void {
  panelCache = cache;
  notifyHeaderFeedQuoteRefreshed(cache.fetchedAt);
  const columns = resolveWebTableColumns(TABLE_ID);
  const filtered = sortOptionsRows(applyFilters(cache.rows, state));
  const summary = computeSummary(cache.rows);

  const tbody = panel.querySelector<HTMLElement>("#wheelPositionsBody");
  if (tbody && !patchOptionsTableBody(tbody, filtered, columns)) {
    tbody.innerHTML = renderTable(filtered, columns);
  }

  const kpiRow = panel.querySelector<HTMLElement>(".kpi-row");
  if (kpiRow) patchKpiRow(kpiRow, summary);

  const note = panel.querySelector<HTMLElement>(".recs-filter-note");
  if (note) {
    note.textContent = `${filtered.length} open legs · put collateral ${fmtMoney(summary.putCollateral)} (cash) · call collateral ${fmtMoney(summary.callCollateral)} (shares)`;
  }

  applyAdviceMarks(panel, "options");
  paintOptionsSortHeaders(panel);
}

function mountPanel(
  panel: HTMLElement,
  state: OptionsFilterState,
  cache: OptionsPanelCache,
  stale = false,
): void {
  let showStale = stale;
  let liveCache = cache;
  const applyPanelCache = (next: OptionsPanelCache): void => {
    panelCache = next;
    liveCache = next;
    notifyHeaderFeedQuoteRefreshed(next.fetchedAt);
    if (mountedPanel !== panel) return;
    if (panelHasDisplayedContent(panel)) {
      softUpdatePanel(panel, state, next);
      return;
    }
    rerender();
  };
  const rerender = (): void => {
    optionsRerender = rerender;
    refreshRowMarks(liveCache.rows);
    withPreservedInputFocus(() => {
      const columns = resolveWebTableColumns(TABLE_ID);
      const filtered = sortOptionsRows(applyFilters(liveCache.rows, state));
      panel.innerHTML = renderPanelShell(state, liveCache, filtered, columns, showStale);
      showStale = false;
      bindFilters(panel, state, rerender);
      bindOptionsHeaderSort(panel, rerender);
      bindScreenerAdvice(panel, "options");
      bindDetailsCollapsiblePersistence(panel, "web-options");
      const resetBtn = panel.querySelector<HTMLElement>("#wheelOptResetFilters");
      if (resetBtn) {
        ensureColumnPickerBesideReset(resetBtn, {
          tableId: TABLE_ID,
          title: "Configure Options columns",
          defaults: getTableColumnDefaults(TABLE_ID),
          onApply: () => rerender(),
        });
      }
    });
  };
  rerender();
}

export async function renderNativeOptionsPanel(
  panel: HTMLElement,
  opts: { force?: boolean } = {},
): Promise<void> {
  const force = opts.force ?? false;
  const state = loadFilters();
  mountedPanel = panel;
  void ensureLedgerMarkIndex(force).catch(() => null);

  const hasCached = Boolean(panelCache?.rows.length);
  if (!hasCached && !panelHasDisplayedContent(panel)) {
    panel.innerHTML = `<div class="sfe-web-panel-loading">Loading open option positions…</div>`;
  }

  try {
    if (hasCached && !force) {
      mountPanel(panel, state, panelCache!);
      if (
        panelCacheNeedsSessionRefresh(panelCache) ||
        Date.now() - panelCache!.fetchedAt >= CACHE_MS ||
        optionsCacheNeedsStockAvgs(panelCache!)
      ) {
        void loadPanelCache(
          panelCacheNeedsSessionRefresh(panelCache) || optionsCacheNeedsStockAvgs(panelCache!),
        ).then((cache) => {
          if (mountedPanel !== panel) return;
          if (panelHasDisplayedContent(panel)) softUpdatePanel(panel, state, cache);
          else mountPanel(panel, state, cache);
        });
      }
      return;
    }

    if (panelCache?.rows.length && panelHasDisplayedContent(panel)) {
      try {
        const cache = await loadPanelCache(force);
        if (mountedPanel !== panel) return;
        softUpdatePanel(panel, state, cache);
        return;
      } catch {
        /* keep showing current rows */
        return;
      }
    }

    if (panelCache?.rows.length) {
      mountPanel(panel, state, panelCache, true);
    }

    const cache = await loadPanelCache(force);
    if (mountedPanel !== panel) return;
    mountPanel(panel, state, cache);
  } catch (err) {
    if (mountedPanel !== panel) return;
    paintPanelLoadError(panel, err, {
      title: "Options · open positions &amp; performance",
      view: "options",
      fallback: "Could not load option positions.",
    });
  }
}

export async function refreshNativeOptionsPanelInBackground(panel: HTMLElement): Promise<void> {
  mountedPanel = panel;
  invalidateWheelSnapshots();
  const state = loadFilters();
  if (panelCache?.rows.length && panelHasDisplayedContent(panel)) {
    try {
      const cache = await loadPanelCache(true);
      if (mountedPanel === panel) softUpdatePanel(panel, state, cache);
    } catch {
      /* keep showing current rows */
    }
    return;
  }
  await renderNativeOptionsPanel(panel, { force: true });
}

export function invalidateOptionsPanelCache(): void {
  panelCache = null;
  panelInflight = null;
  invalidateWheelSnapshots();
}
